/**
 * Score calculation and tier mapping.
 * Used by results display, Brevo (server), and section nav.
 */
import { ChecklistState, ScoreData, ChecklistSection } from '../types';
import { SCORE_CONFIG, TIER_THRESHOLDS } from '../constants';

/**
 * Type guard to check if a value is a valid rating (0-3)
 */
function isValidRating(rating: unknown): rating is number {
  return (
    typeof rating === 'number' &&
    Number.isInteger(rating) &&
    rating >= 0 &&
    rating <= SCORE_CONFIG.maxItemScore
  );
}

/** Returns tier level 1–4 from total score (1 = Not Yet AI-Ready, 4 = AI-Native). */
export function getTierByScore(total: number): 4 | 3 | 2 | 1 {
  // Validate input
  if (typeof total !== 'number' || !Number.isFinite(total)) {
    return 1;
  }

  if (total >= TIER_THRESHOLDS.tier4Min) return 4;
  if (total >= TIER_THRESHOLDS.tier3Min) return 3;
  if (total >= TIER_THRESHOLDS.tier2Min) return 2;
  return 1;
}

/** Default score data for invalid inputs */
const DEFAULT_SCORE_DATA: ScoreData = {
  total: 0,
  maxScore: 0,
  percentage: 0,
  rated: 0,
  bySection: {},
};

/** Computes total score, rated count, percentage, and per-section breakdown from checklist state. */
export function calculateScore(
  state: ChecklistState,
  sections: ChecklistSection[]
): ScoreData {
  // Validate inputs
  if (!state || typeof state !== 'object' || Array.isArray(state)) {
    return DEFAULT_SCORE_DATA;
  }
  if (!Array.isArray(sections)) {
    return DEFAULT_SCORE_DATA;
  }

  let totalScore = 0;
  let ratedCount = 0;
  const maxPossibleScore = sections.reduce(
    (sum, section) => {
      // Validate section has items array
      if (!section || !Array.isArray(section.items)) {
        return sum;
      }
      return sum + section.items.length * SCORE_CONFIG.maxItemScore;
    },
    0
  );
  const bySection: ScoreData['bySection'] = {};

  sections.forEach((section) => {
    // Skip invalid sections
    if (!section || !Array.isArray(section.items) || typeof section.id !== 'string') {
      return;
    }

    let sectionScore = 0;
    let sectionRated = 0;
    const sectionTotal = section.items.length;

    section.items.forEach((item) => {
      // Validate item has required properties
      if (!item || typeof item.id !== 'string') {
        return;
      }

      const rating = state[item.id]?.rating;
      // Use type guard for rating validation
      if (isValidRating(rating)) {
        totalScore += rating;
        sectionScore += rating;
        ratedCount++;
        sectionRated++;
      }
    });

    bySection[section.id] = {
      score: sectionScore,
      rated: sectionRated,
      total: sectionTotal,
    };
  });

  return {
    total: totalScore,
    maxScore: maxPossibleScore,
    percentage: maxPossibleScore > 0 ? Math.round((totalScore / maxPossibleScore) * 100) : 0,
    rated: ratedCount,
    bySection,
  };
}
